"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, chakra, Icon } from "@chakra-ui/react";
import { createCalendar, getLocalTimeZone, parseDate } from "@internationalized/date";
import { Calendar } from "iconoir-react";
import React, { useRef } from "react";
import { useButton, useDatePicker, useLocale } from "react-aria";
import { useCalendarState, useDatePickerState } from "react-stately";
import { CalendarPopup } from "./CalendarPopup";
import { getSizeStyles } from "./utils";
const TriggerButton = chakra("button");
export const DatePicker = React.forwardRef((props, ref) => {
    const { value, defaultValue, onChange, isDisabled, isInvalid, isRequired, placeholder = "Select date", size = "md", variant = "outline", minValue, maxValue, colorPalette = "gray", "aria-label": ariaLabel = "Select date", "aria-labelledby": ariaLabelledby, name, ...restProps } = props;
    const { locale } = useLocale();
    const triggerRef = useRef(null);
    // Parse string dates to CalendarDate objects
    const parsedValue = value ? parseDate(value) : undefined;
    const parsedDefaultValue = defaultValue ? parseDate(defaultValue) : undefined;
    const parsedMinValue = minValue ? parseDate(minValue) : undefined;
    const parsedMaxValue = maxValue ? parseDate(maxValue) : undefined;
    // Cast through `any` to avoid #private field mismatch when
    // multiple copies of @internationalized/date are resolved
    const state = useDatePickerState({
        value: parsedValue,
        defaultValue: parsedDefaultValue,
        onChange: (date) => {
            const dateString = date ? date.toString() : null;
            onChange?.(dateString);
        },
        isDisabled,
        isRequired,
        minValue: parsedMinValue,
        maxValue: parsedMaxValue,
    });
    // Create calendar state separately
    const calendarState = useCalendarState({
        value: state.value,
        onChange: state.setValue,
        createCalendar,
        locale,
        minValue: parsedMinValue,
        maxValue: parsedMaxValue,
    });
    const { groupProps, buttonProps } = useDatePicker({
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
    }, state, triggerRef);
    const { buttonProps: triggerButtonProps } = useButton(buttonProps, triggerRef);
    const formatDate = (date) => {
        if (!date)
            return "";
        return new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(date.toDate(getLocalTimeZone()));
    };
    const sizeStyles = getSizeStyles(size);
    const handleClose = () => {
        state.setOpen(false);
    };
    return (_jsxs(Box, { ref: ref, position: "relative", width: "100%", colorPalette: colorPalette, ...groupProps, ...restProps, children: [_jsxs(TriggerButton, { ref: triggerRef, rounded: { base: "md", md: "lg" }, width: "100%", minWidth: 0, outline: "transparent solid 2px", outlineOffset: "0.5px", position: "relative", appearance: "none", transitionProperty: "common", transitionDuration: "normal", borderWidth: "1px", borderColor: isInvalid ? "border.error"
                    : variant === "subtle" ?
                        "border.emphasized"
                        : "border", bg: variant === "subtle" ? "bg.subtle" : "bg", cursor: isDisabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", opacity: isDisabled ? 0.4 : 1, color: state.value ? "fg" : "fg.muted", _hover: !isDisabled ?
                    {
                        borderColor: isInvalid ? "border.error" : "border.emphasized",
                    }
                    : {}, _focus: {
                    outline: "2px solid",
                    outlineOffset: "0.5px",
                    outlineColor: "colorPalette.focusRing/15",
                    zIndex: 1,
                    borderColor: "colorPalette.focusRing",
                    boxShadow: "0 0 0 1px var(--chakra-colors-colorPalette-focusRing)",
                }, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, ...triggerButtonProps, ...sizeStyles, children: [_jsx(Box, { flex: 1, textAlign: "left", children: state.value ? formatDate(state.value) : placeholder }), _jsx(Icon, { boxSize: 5, color: "colorPalette.solid", children: _jsx(Calendar, {}) })] }), name && (_jsx("input", { type: "hidden", name: name, value: state.value ? state.value.toString() : "", disabled: isDisabled, readOnly: true })), _jsx(CalendarPopup, { datePickerState: state, calendarState: calendarState, isOpen: state.isOpen, onClose: handleClose, colorPalette: colorPalette })] }));
});
DatePicker.displayName = "DatePicker";
